// Photos are resized/compressed in the browser before upload (PRODUCT_DECISIONS "Data efficiency",
// ruling 4, 2026-11-02). The ONE implementation for both apps — every upload path calls this right
// before handing the file to storage or the portal-files edge function.
//
// Rules:
// - Only raster photos are touched (JPEG, PNG, WebP, HEIC/HEIF, BMP). PDFs, documents, GIFs
//   (animation), SVGs and anything else pass through unchanged.
// - Small files pass through unchanged (minBytes).
// - Orientation: the image is decoded with its EXIF orientation applied, so the pixels are stored
//   upright; re-encoding drops the EXIF block (including any GPS position).
// - If the browser cannot decode the file (HEIC on most non-Apple browsers), it is uploaded as-is.
// - A PNG with transparency stays PNG (logos); everything else becomes JPEG.
// - The result is used only when it is actually smaller than the original.
// - Never throws: any failure returns the original file.

const COMPRESSIBLE = /^image\/(jpeg|jpg|pjpeg|png|webp|heic|heif|bmp)$/i;
const COMPRESSIBLE_EXT = /\.(jpe?g|png|webp|heic|heif|bmp)$/i;

export const PHOTO_UPLOAD_DEFAULTS = Object.freeze({
  maxDimension: 2048,
  quality: 0.8,
  minBytes: 300 * 1024,
});

// Company logos: kept sharp — larger floor, higher quality.
export const LOGO_UPLOAD_OPTIONS = Object.freeze({
  maxDimension: 2048,
  quality: 0.92,
  minBytes: 1024 * 1024,
});

function isCompressible(file) {
  if (!file || typeof file.size !== 'number') return false;
  if (file.type) return COMPRESSIBLE.test(file.type);
  return COMPRESSIBLE_EXT.test(file.name || '');
}

// <img> first: Chrome 81+, Firefox 77+ and Safari 13.1+ all apply the EXIF orientation when an
// <img> is drawn to a canvas (CSS image-orientation: from-image is the default), and Safari decodes
// HEIC this way. createImageBitmap's imageOrientation option is less consistently honoured, so it
// is only the fallback.
async function decode(file) {
  if (typeof Image !== 'undefined' && typeof URL !== 'undefined') {
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
      await img.decode();
      return { source: img, width: img.naturalWidth, height: img.naturalHeight, done: () => URL.revokeObjectURL(url) };
    } catch (_) {
      URL.revokeObjectURL(url);
    }
  }
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bmp, width: bmp.width, height: bmp.height, done: () => bmp.close() };
    } catch (_) {
      // undecodable here (e.g. HEIC outside Safari) — caller uploads the original
    }
  }
  return null;
}

function hasTransparency(ctx, w, h) {
  try {
    const { data } = ctx.getImageData(0, 0, w, h);
    for (let i = 3; i < data.length; i += 4) if (data[i] < 255) return true;
    return false;
  } catch (_) {
    return true; // can't tell — keep PNG so nothing goes black
  }
}

function toBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    try { canvas.toBlob((b) => resolve(b), type, quality); } catch (_) { resolve(null); }
  });
}

function renamed(name, ext) {
  const base = (name || 'photo').replace(/\.[^./\\]+$/, '');
  return `${base}.${ext}`;
}

/**
 * Downscale/re-encode a photo before upload. Returns a File (the original when nothing is gained).
 * @param {File|Blob} file
 * @param {{maxDimension?:number, quality?:number, minBytes?:number}} [options]
 */
export async function compressImageForUpload(file, options = {}) {
  const { maxDimension, quality, minBytes } = { ...PHOTO_UPLOAD_DEFAULTS, ...options };
  try {
    if (!isCompressible(file) || file.size <= minBytes) return file;
    if (typeof document === 'undefined') return file;

    const decoded = await decode(file);
    if (!decoded) return file;
    const { source, width: srcW, height: srcH } = decoded;
    let ctx;
    let w;
    let h;
    let canvas;
    try {
      if (!srcW || !srcH) return file;
      const scale = Math.min(1, maxDimension / Math.max(srcW, srcH));
      w = Math.max(1, Math.round(srcW * scale));
      h = Math.max(1, Math.round(srcH * scale));
      canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      ctx = canvas.getContext('2d');
      if (!ctx) return file;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(source, 0, 0, w, h);
    } finally {
      decoded.done();
    }

    const keepPng = /png/i.test(file.type || file.name || '') && hasTransparency(ctx, w, h);
    let blob;
    if (keepPng) {
      blob = await toBlob(canvas, 'image/png');
    } else {
      // JPEG has no alpha; paint transparent areas white rather than black.
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      blob = await toBlob(canvas, 'image/jpeg', quality);
    }
    if (!blob || blob.size >= file.size) return file;

    const type = keepPng ? 'image/png' : 'image/jpeg';
    const name = renamed(file.name, keepPng ? 'png' : 'jpg');
    if (typeof File === 'function') {
      return new File([blob], name, { type, lastModified: file.lastModified || Date.now() });
    }
    blob.name = name;
    return blob;
  } catch (_) {
    return file;
  }
}
